import {
  type BaseType,
  type Props,
  type ValidatorSync,
  typeMismatchError,
} from "./base";
import * as yup from "yup";

export interface NumberType extends BaseType {
  type: "NUMBER";
  /**
   * Min number value.
   */
  min?: number;
  /**
   * Max number value.
   */
  max?: number;

  /**
   * WARNING: Javascript number looses precision after ~16 digits.
   * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/MAX_SAFE_INTEGER
   */
  precision?: number;
}

export function number(props?: Props<NumberType>): NumberType {
  return {
    ...props,
    type: "NUMBER",
  };
}

export const validator: ValidatorSync<NumberType> = (type, options) => {
  if (options?.extendedTypesValidationsV2) {
    return handRolledValidator(type);
  }
  return yupValidator(type);
};

const yupValidator = (type: Readonly<NumberType>) => {
  let schema = yup.number();

  if (typeof type.min === "number") schema = schema.min(type.min);
  if (typeof type.max === "number") schema = schema.max(type.max);

  return (value: unknown) => {
    schema.validateSync(value, { strict: true });
  };
};

const handRolledValidator = (type: Readonly<NumberType>) => {
  return (value: unknown) => {
    const unboxed = value instanceof Number ? value.valueOf() : value;
    if (typeof unboxed !== "number" || Number.isNaN(unboxed)) {
      throw typeMismatchError("number", value);
    }

    if (typeof type.min === "number" && unboxed < type.min) {
      throw new Error(`this must be greater than or equal to ${type.min}`);
    }

    if (typeof type.max === "number" && unboxed > type.max) {
      throw new Error(`this must be less than or equal to ${type.max}`);
    }
  };
};
